import { TransactionRevertedError, TransactionRejectedError } from "@gelatocloud/gasless";
import { t } from "@lingui/macro";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useLatest } from "react-use";

import { isDevelopment } from "config/env";
import { useSettings } from "context/SettingsContext/SettingsContextProvider";
import { useSubaccountContext } from "context/SubaccountContext/SubaccountContextProvider";
import { useTokenPermitsContext } from "context/TokenPermitsContext/TokenPermitsContextProvider";
import { useTokensBalancesUpdates } from "context/TokensBalancesContext/TokensBalancesContextProvider";
import {
  subscribeToApprovalEvents,
  subscribeToTransferEvents,
  subscribeToV2Events,
} from "context/WebsocketContext/subscribeToEvents";
import { MultichainTransferProgress } from "domain/multichain/progress/MultichainTransferProgress";
import { useMultichainTransferProgressView } from "domain/multichain/progress/MultichainTransferProgressView";
import { MarketsInfoData, useMarketsInfoRequest } from "domain/synthetics/markets";
import { isGlvEnabled } from "domain/synthetics/markets/glv";
import { useGlvMarketsInfo } from "domain/synthetics/markets/useGlvMarkets";
import {
  DecreasePositionSwapType,
  isDecreaseOrderType,
  isIncreaseOrderType,
  isLiquidationOrderType,
  isMarketOrderType,
  isSwapOrderType,
  OrderTxnType,
} from "domain/synthetics/orders";
import { getPositionKey } from "domain/synthetics/positions";
import { useTokensDataRequest } from "domain/synthetics/tokens";
import { getSwapPathOutputAddresses } from "domain/synthetics/trade";
import {
  applyOrderBackfillMatches,
  getIsPendingOrderBackfillable,
  ORDER_BACKFILL_MAX_AGE_MS,
  OrderBackfillMatch,
} from "domain/synthetics/tradeHistory/orderStatusesBackfill";
import { useOrderStatusesBackfill } from "domain/synthetics/tradeHistory/useOrderStatusesBackfill";
import { TokenBalanceType } from "domain/tokens";
import { useChainId } from "lib/chains";
import { pushErrorNotification, pushSuccessNotification } from "lib/contracts";
import { getIsInsufficientExecutionFeeError, getIsInvalidSignatureError } from "lib/errors/customErrors";
import { helperToast } from "lib/helperToast";
import {
  getGLVSwapMetricId,
  getGMSwapMetricId,
  getPositionOrderMetricId,
  getShiftGMMetricId,
  getSwapOrderMetricId,
  sendOrderCancelledMetric,
  sendOrderCreatedMetric,
  sendOrderExecutedMetric,
  sendTxnErrorMetric,
} from "lib/metrics/utils";
import { formatTokenAmount, formatUsd } from "lib/numbers";
import { deleteByKey, getByKey, setByKey, updateByKey } from "lib/objects";
import { getProvider } from "lib/rpc";
import { sleep } from "lib/sleep";
import { getTenderlyAccountParams } from "lib/tenderly";
import { getGelatoRelayerForChain, getGelatoTaskDebugInfo } from "lib/transactions/sendExpressTransaction";
import { useHasLostFocus } from "lib/useHasPageLostFocus";
import { sendUserAnalyticsOrderResultEvent, userAnalytics } from "lib/userAnalytics";
import { TokenApproveResultEvent } from "lib/userAnalytics/types";
import useWallet from "lib/wallets/useWallet";
import { getToken, getWrappedToken, NATIVE_TOKEN_ADDRESS } from "sdk/configs/tokens";
import { StatusCode } from "sdk/utils/gelatoRelay";
import { decodeOrderTwapParams } from "sdk/utils/twap/uiFeeReceiver";

import { getInsufficientExecutionFeeToastContent, InvalidSignatureToastContent } from "components/Errors/errorToasts";
import { FeesSettlementStatusNotification } from "components/StatusNotification/FeesSettlementStatusNotification";
import { GmStatusNotification } from "components/StatusNotification/GmStatusNotification";
import { OrdersStatusNotificiation } from "components/StatusNotification/OrderStatusNotification";

import {
  ApprovalStatuses,
  DepositCreatedEventData,
  DepositStatuses,
  EventLogData,
  EventTxnParams,
  GelatoTaskStatus,
  GLVDepositCreatedEventData,
  OrderCreatedEventData,
  OrderStatuses,
  PendingDepositData,
  PendingExpressTxnParams,
  PendingFundingFeeSettlementData,
  PendingOrderData,
  PendingOrdersUpdates,
  PendingPositionsUpdates,
  PendingPositionUpdate,
  PendingShiftData,
  PendingWithdrawalData,
  PositionDecreaseEvent,
  PositionIncreaseEvent,
  ShiftCreatedEventData,
  ShiftStatuses,
  SyntheticsEventsContextType,
  WithdrawalCreatedEventData,
  WithdrawalStatuses,
} from "./types";
import { useMultichainEvents } from "./useMultichainEvents";
import { extractGelatoError, getGelatoTaskUrl, getPendingOrderKey } from "./utils";

const SyntheticsEventsContext = createContext({});

export function useSyntheticsEvents(): SyntheticsEventsContextType {
  return useContext(SyntheticsEventsContext) as SyntheticsEventsContextType;
}

export function SyntheticsEventsProvider({ children }: { children: ReactNode }) {
  const { chainId, srcChainId } = useChainId();
  const { account: currentAccount } = useWallet();
  const provider = getProvider(undefined, chainId);
  const { hasV2LostFocus, hasPageLostFocus } = useHasLostFocus();
  const { executionFeeBufferBps, setIsSettingsVisible } = useSettings();

  const { resetTokenPermits } = useTokenPermitsContext();
  const { refreshSubaccountData } = useSubaccountContext();
  const { tokensData } = useTokensDataRequest(chainId, srcChainId);
  const { marketsInfoData } = useMarketsInfoRequest(chainId, { tokensData });

  const { glvData } = useGlvMarketsInfo(isGlvEnabled(chainId), {
    marketsInfoData,
    tokensData,
    chainId,
    srcChainId,
    account: currentAccount,
  });

  const { glvAndGmMarketsData, marketTokensAddressesString } = useMemo(() => {
    const glvAndGmMarketsData = {
      ...marketsInfoData,
      ...glvData,
    };

    const marketTokensAddressesString = Object.keys(glvAndGmMarketsData).join("-");

    return {
      glvAndGmMarketsData,
      marketTokensAddressesString,
    };
  }, [marketsInfoData, glvData]);

  const [orderStatuses, setOrderStatuses] = useState<OrderStatuses>({});
  const [depositStatuses, setDepositStatuses] = useState<DepositStatuses>({});
  const [withdrawalStatuses, setWithdrawalStatuses] = useState<WithdrawalStatuses>({});
  const [shiftStatuses, setShiftStatuses] = useState<ShiftStatuses>({});

  const { setWebsocketTokenBalancesUpdates, setOptimisticTokensBalancesUpdates } = useTokensBalancesUpdates();
  const [approvalStatuses, setApprovalStatuses] = useState<ApprovalStatuses>({});

  const [pendingOrdersUpdates, setPendingOrdersUpdates] = useState<PendingOrdersUpdates>({});
  const [pendingPositionsUpdates, setPendingPositionsUpdates] = useState<PendingPositionsUpdates>({});
  const [awaitingBackfillOrders, setAwaitingBackfillOrders] = useState<PendingOrderData[]>([]);
  const [positionIncreaseEvents, setPositionIncreaseEvents] = useState<PositionIncreaseEvent[]>([]);
  const [positionDecreaseEvents, setPositionDecreaseEvents] = useState<PositionDecreaseEvent[]>([]);
  const [gelatoTaskStatuses, setGelatoTaskStatuses] = useState<{ [taskId: string]: GelatoTaskStatus }>({});
  const [pendingExpressTxnParams, setPendingExpressTxnParams] = useState<{
    [key: string]: Partial<PendingExpressTxnParams>;
  }>({});
  const latestPendingExpressTxnParams = useLatest(pendingExpressTxnParams);
  const latestGelatoTaskStatuses = useLatest(gelatoTaskStatuses);
  const pendingOrderToastIdRef = useRef<number>();
  const eventLogHandlers = useRef({});

  const handleExpressTxnSuccess = useCallback(
    (pendingExpressTxn: Partial<PendingExpressTxnParams>) => {
      const key = pendingExpressTxn.key;

      if (!key) {
        return;
      }

      refreshSubaccountData();

      if (pendingExpressTxn?.tokenPermits?.length) {
        resetTokenPermits();
      }
    },
    [refreshSubaccountData, resetTokenPermits]
  );

  const updateNativeTokenBalance = useCallback(() => {
    if (!currentAccount) {
      return;
    }

    provider.getBalance(currentAccount, "pending").then((balance) => {
      setWebsocketTokenBalancesUpdates((old) =>
        setByKey(old, NATIVE_TOKEN_ADDRESS, {
          balanceType: TokenBalanceType.Wallet,
          balance,
        })
      );
    });
  }, [currentAccount, provider, setWebsocketTokenBalancesUpdates]);

  // use ref to avoid re-subscribing on state changes
  eventLogHandlers.current = {
    OrderCreated: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const uiFeeReceiver = eventData.addressItems.items.uiFeeReceiver;
      const twapParams = decodeOrderTwapParams(eventData.bytes32Items.arrayItems.dataList, uiFeeReceiver);

      const data: OrderCreatedEventData = {
        account: eventData.addressItems.items.account,
        receiver: eventData.addressItems.items.receiver,
        callbackContract: eventData.addressItems.items.callbackContract,
        marketAddress: eventData.addressItems.items.market,
        initialCollateralTokenAddress: eventData.addressItems.items.initialCollateralToken,
        swapPath: eventData.addressItems.arrayItems.swapPath,
        sizeDeltaUsd: eventData.uintItems.items.sizeDeltaUsd,
        initialCollateralDeltaAmount: eventData.uintItems.items.initialCollateralDeltaAmount,
        contractTriggerPrice: eventData.uintItems.items.triggerPrice,
        contractAcceptablePrice: eventData.uintItems.items.acceptablePrice,
        executionFee: eventData.uintItems.items.executionFee,
        callbackGasLimit: eventData.uintItems.items.callbackGasLimit,
        minOutputAmount: eventData.uintItems.items.minOutputAmount,
        updatedAtBlock: eventData.uintItems.items.updatedAtBlock,
        orderType: Number(eventData.uintItems.items.orderType),
        decreasePositionSwapType: Number(
          eventData.uintItems.items.decreasePositionSwapType ?? DecreasePositionSwapType.NoSwap
        ) as DecreasePositionSwapType,
        isLong: eventData.boolItems.items.isLong,
        shouldUnwrapNativeToken: eventData.boolItems.items.shouldUnwrapNativeToken,
        isFrozen: eventData.boolItems.items.isFrozen,
        uiFeeReceiver: uiFeeReceiver,
        externalSwapQuote: undefined,
        key: eventData.bytes32Items.items.key,
        isTwap: twapParams !== undefined,
      };

      if (data.account !== currentAccount) {
        return;
      }

      const metricId = isSwapOrderType(data.orderType) ? getSwapOrderMetricId(data) : getPositionOrderMetricId(data);
      sendOrderCreatedMetric(metricId);

      if (!isMarketOrderType(data.orderType)) {
        sendUserAnalyticsOrderResultEvent(
          chainId,
          metricId,
          true,
          undefined,
          twapParams ? `createOrder:${twapParams.twapId}` : undefined
        );
      }

      setOrderStatuses((old) =>
        setByKey(old, data.key, {
          ...old[data.key],
          key: data.key,
          data,
          createdTxnHash: txnParams.transactionHash,
          createdAt: old[data.key]?.createdAt ?? Date.now(),
        })
      );

      const pendingOrderKey = getPendingOrderKey(data);
      const pendingExpressTxn = Object.values(latestPendingExpressTxnParams.current).find((p) =>
        p.pendingOrdersKeys?.includes(pendingOrderKey)
      );

      if (pendingExpressTxn) {
        handleExpressTxnSuccess(pendingExpressTxn);
      }

      setPendingOrdersUpdates((old) => deleteByKey(old, data.key));

      setAwaitingBackfillOrders((old) => {
        const index = old.findIndex(
          (order) => order.txnType === "create" && getPendingOrderKey(order) === pendingOrderKey
        );

        return index === -1 ? old : old.filter((_, i) => i !== index);
      });

      if (isMarketOrderType(data.orderType) && marketsInfoData) {
        const pendingPositionKey = getPendingPositionKeyFromOrder(chainId, marketsInfoData, data);

        if (pendingPositionKey) {
          setPendingPositionsUpdates((old) =>
            old[pendingPositionKey] ? updateByKey(old, pendingPositionKey, { orderKey: data.key }) : old
          );
        }
      }
    },

    OrderUpdated: (eventData: EventLogData, txnParams: EventTxnParams) => {
      const key = eventData.bytes32Items.items.key;
      const account = eventData.addressItems.items.account;

      if (account !== currentAccount) {
        return;
      }

      setOrderStatuses((old) => {
        if (old[key]) {
          return updateByKey(old, key, {
            updatedTxnHash: txnParams.transactionHash,
            isViewed: false,
          });
        } else {
          return setByKey(old, key, {
            key,
            createdAt: Date.now(),
            updatedTxnHash: txnParams.transactionHash,
          });
        }
      });

      setPendingOrdersUpdates((old) => deleteByKey(old, key));

      setAwaitingBackfillOrders((old) =>
        old.filter((order) => !(order.txnType === "update" && order.orderKey === key))
      );
    },

    OrderExecuted: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const key = eventData.bytes32Items.items.key;

      const order = orderStatuses[key]?.data;

      if (order) {
        const metricId = isSwapOrderType(order.orderType)
          ? getSwapOrderMetricId(order)
          : getPositionOrderMetricId(order);

        sendOrderExecutedMetric(metricId);
        if (!order.isTwap) {
          sendUserAnalyticsOrderResultEvent(chainId, metricId, true);
        }
      }

      setOrderStatuses((old) => {
        if (!old[key]) {
          // Avoid tracking unrelated background executions.
          if (awaitingBackfillOrders.length === 0) {
            return old;
          }

          return setByKey(old, key, {
            key,
            createdAt: Date.now(),
            executedTxnHash: txnParams.transactionHash,
          });
        }

        return updateByKey(old, key, { executedTxnHash: txnParams.transactionHash });
      });
    },

    OrderCancelled: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const key = eventData.bytes32Items.items.key;
      const account = eventData.addressItems.items.account;

      if (account !== currentAccount) {
        return;
      }

      setOrderStatuses((old) => {
        if (old[key]) {
          return updateByKey(old, key, {
            cancelledTxnHash: txnParams.transactionHash,
            isViewed: false,
          });
        } else {
          return setByKey(old, key, {
            key,
            createdAt: Date.now(),
            cancelledTxnHash: txnParams.transactionHash,
          });
        }
      });

      setAwaitingBackfillOrders((old) => old.filter((awaitingOrder) => awaitingOrder.orderKey !== key));

      const order = orderStatuses[key]?.data;

      if (order) {
        const metricId = isSwapOrderType(order.orderType)
          ? getSwapOrderMetricId(order)
          : getPositionOrderMetricId(order);

        sendOrderCancelledMetric(metricId, eventData);
        sendUserAnalyticsOrderResultEvent(chainId, metricId, false);
      }

      // If pending user order is cancelled, reset the pending position state
      if (order && marketsInfoData) {
        const pendingPositionKey = getPendingPositionKeyFromOrder(chainId, marketsInfoData, order);

        if (pendingPositionKey) {
          setPendingPositionsUpdates((old) => setByKey(old, pendingPositionKey, undefined));
        }
      }
    },

    GlvDepositCreated: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const depositData: GLVDepositCreatedEventData = {
        account: eventData.addressItems.items.account,
        receiver: eventData.addressItems.items.receiver,
        callbackContract: eventData.addressItems.items.callbackContract,
        glvAddress: eventData.addressItems.items.glv,
        marketAddress: eventData.addressItems.items.market,
        initialLongTokenAddress: eventData.addressItems.items.initialLongToken,
        initialShortTokenAddress: eventData.addressItems.items.initialShortToken,
        longTokenSwapPath: eventData.addressItems.arrayItems.longTokenSwapPath,
        shortTokenSwapPath: eventData.addressItems.arrayItems.shortTokenSwapPath,
        initialLongTokenAmount: eventData.uintItems.items.initialLongTokenAmount,
        initialShortTokenAmount: eventData.uintItems.items.initialShortTokenAmount,
        initialMarketTokenAmount: eventData.uintItems.items.marketTokenAmount,
        minMarketTokens: eventData.uintItems.items.minGlvTokens,
        updatedAtBlock: eventData.uintItems.items.updatedAtBlock,
        executionFee: eventData.uintItems.items.executionFee,
        callbackGasLimit: eventData.uintItems.items.callbackGasLimit,
        shouldUnwrapNativeToken: eventData.boolItems.items.shouldUnwrapNativeToken,
        key: eventData.bytes32Items.items.key,
        isGlvDeposit: true,
      };

      if (depositData.account !== currentAccount) {
        return;
      }

      const metricId = getGLVSwapMetricId(depositData);

      sendOrderCreatedMetric(metricId);

      setDepositStatuses((old) =>
        setByKey(old, depositData.key, {
          key: depositData.key,
          data: depositData,
          createdTxnHash: txnParams.transactionHash,
          createdAt: Date.now(),
        })
      );
    },

    DepositCreated: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const depositData: DepositCreatedEventData = {
        account: eventData.addressItems.items.account,
        receiver: eventData.addressItems.items.receiver,
        callbackContract: eventData.addressItems.items.callbackContract,
        marketAddress: eventData.addressItems.items.market,
        initialLongTokenAddress: eventData.addressItems.items.initialLongToken,
        initialShortTokenAddress: eventData.addressItems.items.initialShortToken,
        longTokenSwapPath: eventData.addressItems.arrayItems.longTokenSwapPath,
        shortTokenSwapPath: eventData.addressItems.arrayItems.shortTokenSwapPath,
        initialLongTokenAmount: eventData.uintItems.items.initialLongTokenAmount,
        initialShortTokenAmount: eventData.uintItems.items.initialShortTokenAmount,
        minMarketTokens: eventData.uintItems.items.minMarketTokens,
        updatedAtBlock: eventData.uintItems.items.updatedAtBlock,
        executionFee: eventData.uintItems.items.executionFee,
        callbackGasLimit: eventData.uintItems.items.callbackGasLimit,
        shouldUnwrapNativeToken: eventData.boolItems.items.shouldUnwrapNativeToken,
        key: eventData.bytes32Items.items.key,
        isGlvDeposit: false,
      };

      if (depositData.account !== currentAccount) {
        return;
      }

      const metricId = getGMSwapMetricId(depositData);

      sendOrderCreatedMetric(metricId);

      setDepositStatuses((old) =>
        setByKey(old, depositData.key, {
          key: depositData.key,
          data: depositData,
          createdTxnHash: txnParams.transactionHash,
          createdAt: Date.now(),
        })
      );
    },

    GlvDepositExecuted: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const key = eventData.bytes32Items.items.key;
      if (depositStatuses[key]?.data) {
        const metricId = getGLVSwapMetricId(depositStatuses[key].data! as GLVDepositCreatedEventData);

        sendOrderExecutedMetric(metricId);
        sendUserAnalyticsOrderResultEvent(chainId, metricId, true);

        setDepositStatuses((old) => updateByKey(old, key, { executedTxnHash: txnParams.transactionHash }));
      }
    },

    DepositExecuted: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const key = eventData.bytes32Items.items.key;

      if (depositStatuses[key]?.data) {
        const metricId = getGMSwapMetricId(depositStatuses[key].data!);

        sendOrderExecutedMetric(metricId);
        sendUserAnalyticsOrderResultEvent(chainId, metricId, true);
        setDepositStatuses((old) => updateByKey(old, key, { executedTxnHash: txnParams.transactionHash }));
      }
    },

    DepositCancelled: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const key = eventData.bytes32Items.items.key;

      if (depositStatuses[key]?.data) {
        const metricId = getGMSwapMetricId(depositStatuses[key].data!);

        sendOrderCancelledMetric(metricId, eventData);
        sendUserAnalyticsOrderResultEvent(chainId, metricId, false);
        setDepositStatuses((old) => updateByKey(old, key, { cancelledTxnHash: txnParams.transactionHash }));
      }
    },

    GlvDepositCancelled: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const key = eventData.bytes32Items.items.key;

      if (depositStatuses[key]?.data) {
        const metricId = getGLVSwapMetricId(depositStatuses[key].data! as GLVDepositCreatedEventData);

        sendOrderCancelledMetric(metricId, eventData);
        sendUserAnalyticsOrderResultEvent(chainId, metricId, false);

        setDepositStatuses((old) => updateByKey(old, key, { cancelledTxnHash: txnParams.transactionHash }));
      }
    },

    WithdrawalCreated: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const data: WithdrawalCreatedEventData = {
        account: eventData.addressItems.items.account,
        receiver: eventData.addressItems.items.receiver,
        callbackContract: eventData.addressItems.items.callbackContract,
        marketAddress: eventData.addressItems.items.market,
        marketTokenAmount: eventData.uintItems.items.marketTokenAmount,
        minLongTokenAmount: eventData.uintItems.items.minLongTokenAmount,
        minShortTokenAmount: eventData.uintItems.items.minShortTokenAmount,
        updatedAtBlock: eventData.uintItems.items.updatedAtBlock,
        executionFee: eventData.uintItems.items.executionFee,
        callbackGasLimit: eventData.uintItems.items.callbackGasLimit,
        shouldUnwrapNativeToken: eventData.boolItems.items.shouldUnwrapNativeToken,
        key: eventData.bytes32Items.items.key,
      };

      if (data.account !== currentAccount) {
        return;
      }

      const metricId = getGMSwapMetricId({
        marketAddress: data.marketAddress,
        executionFee: data.executionFee,
      });

      sendOrderCreatedMetric(metricId);

      setWithdrawalStatuses((old) =>
        setByKey(old, data.key, {
          key: data.key,
          data,
          createdTxnHash: txnParams.transactionHash,
          createdAt: Date.now(),
        })
      );
    },

    GlvWithdrawalCreated: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const data: WithdrawalCreatedEventData = {
        account: eventData.addressItems.items.account,
        receiver: eventData.addressItems.items.receiver,
        callbackContract: eventData.addressItems.items.callbackContract,
        marketAddress: eventData.addressItems.items.glv,
        marketTokenAmount: eventData.uintItems.items.glvTokenAmount,
        minLongTokenAmount: eventData.uintItems.items.minLongTokenAmount,
        minShortTokenAmount: eventData.uintItems.items.minShortTokenAmount,
        updatedAtBlock: eventData.uintItems.items.updatedAtBlock,
        executionFee: eventData.uintItems.items.executionFee,
        callbackGasLimit: eventData.uintItems.items.callbackGasLimit,
        shouldUnwrapNativeToken: eventData.boolItems.items.shouldUnwrapNativeToken,
        key: eventData.bytes32Items.items.key,
      };

      if (data.account !== currentAccount) {
        return;
      }

      const metricId = getGLVSwapMetricId({
        glvAddress: data.marketAddress,
        executionFee: data.executionFee,
      });

      sendOrderCreatedMetric(metricId);

      setWithdrawalStatuses((old) =>
        setByKey(old, data.key, {
          key: data.key,
          data,
          createdTxnHash: txnParams.transactionHash,
          createdAt: Date.now(),
        })
      );
    },

    WithdrawalExecuted: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const key = eventData.bytes32Items.items.key;

      if (withdrawalStatuses[key]?.data) {
        const metricId = getGMSwapMetricId({
          marketAddress: withdrawalStatuses[key].data!.marketAddress,
          executionFee: withdrawalStatuses[key].data!.executionFee,
        });
        sendOrderExecutedMetric(metricId);

        setWithdrawalStatuses((old) => updateByKey(old, key, { executedTxnHash: txnParams.transactionHash }));
      }
    },

    GlvWithdrawalExecuted: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const key = eventData.bytes32Items.items.key;
      if (withdrawalStatuses[key]?.data) {
        const metricId = getGLVSwapMetricId({
          glvAddress: withdrawalStatuses[key].data!.marketAddress,
          executionFee: withdrawalStatuses[key].data!.executionFee,
        });
        sendOrderExecutedMetric(metricId);

        setWithdrawalStatuses((old) => updateByKey(old, key, { executedTxnHash: txnParams.transactionHash }));
      }
    },

    WithdrawalCancelled: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const key = eventData.bytes32Items.items.key;

      if (withdrawalStatuses[key]?.data) {
        const metricId = getGLVSwapMetricId({
          glvAddress: withdrawalStatuses[key].data!.marketAddress,
          executionFee: withdrawalStatuses[key].data!.executionFee,
        });
        sendOrderCancelledMetric(metricId, eventData);

        setWithdrawalStatuses((old) => updateByKey(old, key, { cancelledTxnHash: txnParams.transactionHash }));
      }
    },

    GlvWithdrawalCancelled: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const key = eventData.bytes32Items.items.key;

      if (withdrawalStatuses[key]?.data) {
        const metricId = getGMSwapMetricId({
          marketAddress: withdrawalStatuses[key].data!.marketAddress,
          executionFee: withdrawalStatuses[key].data!.executionFee,
        });
        sendOrderCancelledMetric(metricId, eventData);

        setWithdrawalStatuses((old) => updateByKey(old, key, { cancelledTxnHash: txnParams.transactionHash }));
      }
    },

    ShiftCreated: (eventData: EventLogData, txnParams: EventTxnParams) => {
      const data: ShiftCreatedEventData = {
        key: eventData.bytes32Items.items.key,
        account: eventData.addressItems.items.account,
        receiver: eventData.addressItems.items.receiver,
        callbackContract: eventData.addressItems.items.callbackContract,
        fromMarket: eventData.addressItems.items.fromMarket,
        toMarket: eventData.addressItems.items.toMarket,
        marketTokenAmount: eventData.uintItems.items.marketTokenAmount,
        minMarketTokens: eventData.uintItems.items.minMarketTokens,
        updatedAtTime: eventData.uintItems.items.updatedAtTime,
        executionFee: eventData.uintItems.items.executionFee,
      };

      if (data.account !== currentAccount) {
        return;
      }

      const metricId = getShiftGMMetricId({
        fromMarketAddress: data.fromMarket,
        toMarketAddress: data.toMarket,
        executionFee: data.executionFee,
      });

      sendOrderCreatedMetric(metricId);

      setShiftStatuses((old) =>
        setByKey(old, data.key, {
          key: data.key,
          data,
          createdTxnHash: txnParams.transactionHash,
          createdAt: Date.now(),
        })
      );
    },

    ShiftExecuted: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const key = eventData.bytes32Items.items.key;

      if (shiftStatuses[key]?.data) {
        const metricId = getShiftGMMetricId({
          fromMarketAddress: shiftStatuses[key].data!.fromMarket,
          toMarketAddress: shiftStatuses[key].data!.toMarket,
          executionFee: shiftStatuses[key].data!.executionFee,
        });

        sendOrderExecutedMetric(metricId);

        setShiftStatuses((old) => updateByKey(old, key, { executedTxnHash: txnParams.transactionHash }));
      }
    },

    ShiftCancelled: (eventData: EventLogData, txnParams: EventTxnParams) => {
      updateNativeTokenBalance();

      const key = eventData.bytes32Items.items.key;

      if (shiftStatuses[key].data) {
        const metricId = getShiftGMMetricId({
          fromMarketAddress: shiftStatuses[key].data!.fromMarket,
          toMarketAddress: shiftStatuses[key].data!.toMarket,
          executionFee: shiftStatuses[key].data!.executionFee,
        });

        sendOrderCancelledMetric(metricId, eventData);

        setShiftStatuses((old) => updateByKey(old, key, { cancelledTxnHash: txnParams.transactionHash }));
      }
    },

    PositionIncrease: (eventData: EventLogData, txnParams: EventTxnParams) => {
      const data: PositionIncreaseEvent = {
        blockNumber: txnParams.blockNumber,
        positionKey: getPositionKey(
          eventData.addressItems.items.account,
          eventData.addressItems.items.market,
          eventData.addressItems.items.collateralToken,
          eventData.boolItems.items.isLong
        )!,
        contractPositionKey: eventData.bytes32Items.items.positionKey,
        account: eventData.addressItems.items.account,
        marketAddress: eventData.addressItems.items.market,
        collateralTokenAddress: eventData.addressItems.items.collateralToken,
        sizeInUsd: eventData.uintItems.items.sizeInUsd,
        sizeInTokens: eventData.uintItems.items.sizeInTokens,
        collateralAmount: eventData.uintItems.items.collateralAmount,
        borrowingFactor: eventData.uintItems.items.borrowingFactor,
        executionPrice: eventData.uintItems.items.executionPrice,
        sizeDeltaUsd: eventData.uintItems.items.sizeDeltaUsd,
        sizeDeltaInTokens: eventData.uintItems.items.sizeDeltaInTokens,
        longTokenFundingAmountPerSize: eventData.intItems.items.longTokenFundingAmountPerSize,
        shortTokenFundingAmountPerSize: eventData.intItems.items.shortTokenFundingAmountPerSize,
        collateralDeltaAmount: eventData.intItems.items.collateralDeltaAmount,
        isLong: eventData.boolItems.items.isLong,
        increasedAtTime: eventData.uintItems.items.increasedAtTime,
        orderType: Number(eventData.uintItems.items.orderType),
        orderKey: eventData.bytes32Items.items.orderKey,
      };

      if (data.account !== currentAccount) {
        return;
      }

      setPositionIncreaseEvents((old) => [...old, data]);

      // If this is a limit order, or the order status is not received previosly, notify the user
      if (!isMarketOrderType(data.orderType) || !orderStatuses[data.orderKey]) {
        let text = "";

        const marketInfo = getByKey(marketsInfoData, data.marketAddress);
        const indexToken = marketInfo?.indexToken;
        const collateralToken = getToken(chainId, data.collateralTokenAddress);

        if (!marketInfo || !indexToken || !collateralToken) {
          return;
        }

        const longShortText = data.isLong ? t`Long` : t`Short`;
        const positionText = `${indexToken?.symbol} ${longShortText}`;

        if (data.sizeDeltaUsd == 0n) {
          text = t`Deposited ${formatTokenAmount(
            data.collateralDeltaAmount,
            collateralToken.decimals,
            collateralToken.symbol,
            {
              isStable: collateralToken.isStable,
            }
          )} into ${positionText}`;
        } else {
          text = t`Increased ${positionText}, +${formatUsd(data.sizeDeltaUsd)}`;
        }

        pushSuccessNotification(chainId, text, { transactionHash: txnParams.transactionHash });
      }
    },

    PositionDecrease: (eventData: EventLogData, txnParams: EventTxnParams) => {
      const data: PositionDecreaseEvent = {
        blockNumber: txnParams.blockNumber,
        positionKey: getPositionKey(
          eventData.addressItems.items.account,
          eventData.addressItems.items.market,
          eventData.addressItems.items.collateralToken,
          eventData.boolItems.items.isLong
        )!,
        account: eventData.addressItems.items.account,
        marketAddress: eventData.addressItems.items.market,
        collateralTokenAddress: eventData.addressItems.items.collateralToken,
        sizeInUsd: eventData.uintItems.items.sizeInUsd,
        sizeInTokens: eventData.uintItems.items.sizeInTokens,
        sizeDeltaUsd: eventData.uintItems.items.sizeDeltaUsd,
        sizeDeltaInTokens: eventData.uintItems.items.sizeDeltaInTokens,
        collateralAmount: eventData.uintItems.items.collateralAmount,
        collateralDeltaAmount: eventData.intItems.items.collateralDeltaAmount,
        borrowingFactor: eventData.uintItems.items.borrowingFactor,
        longTokenFundingAmountPerSize: eventData.intItems.items.longTokenFundingAmountPerSize,
        shortTokenFundingAmountPerSize: eventData.intItems.items.shortTokenFundingAmountPerSize,
        pnlUsd: eventData.intItems.items.pnlUsd,
        executionPrice: eventData.uintItems.items.executionPrice,
        isLong: eventData.boolItems.items.isLong,
        contractPositionKey: eventData.bytes32Items.items.positionKey,
        decreasedAtTime: eventData.uintItems.items.decreasedAtTime,
        orderType: Number(eventData.uintItems.items.orderType),
        orderKey: eventData.bytes32Items.items.orderKey,
      };

      if (data.account !== currentAccount) {
        return;
      }

      setPositionDecreaseEvents((old) => [...old, data]);

      // If this is a trigger or liquidation order, or the order status is not received previosly, notify the user
      if (!isMarketOrderType(data.orderType) || !orderStatuses[data.orderKey]) {
        let text = "";

        const marketInfo = getByKey(marketsInfoData, data.marketAddress);
        const indexToken = marketInfo?.indexToken;
        const collateralToken = getToken(chainId, data.collateralTokenAddress);

        if (!marketInfo || !indexToken || !collateralToken) {
          return;
        }

        const longShortText = data.isLong ? t`Long` : t`Short`;
        const positionText = `${indexToken?.symbol} ${longShortText}`;

        if (data.sizeDeltaUsd == 0n) {
          text = t`Withdrew ${formatTokenAmount(
            data.collateralDeltaAmount,
            collateralToken.decimals,
            collateralToken.symbol,
            {
              isStable: collateralToken.isStable,
            }
          )} from ${positionText}`;
        } else {
          const orderTypeLabel = isLiquidationOrderType(data.orderType) ? t`Liquidated` : t`Decreased`;
          text = t`${orderTypeLabel} ${positionText}, -${formatUsd(data.sizeDeltaUsd)}`;
        }

        if (isLiquidationOrderType(data.orderType)) {
          pushErrorNotification(chainId, text, { transactionHash: txnParams.transactionHash });
        } else {
          pushSuccessNotification(chainId, text, { transactionHash: txnParams.transactionHash });
        }
      }
    },

    MultichainTransferOut: (eventData: EventLogData) => {
      const token = eventData.addressItems.items.token;
      const amount = eventData.uintItems.items.amount;

      setWebsocketTokenBalancesUpdates((old) => {
        const oldDiff = old[token]?.diff || 0n;
        return setByKey(old, token, {
          balanceType: TokenBalanceType.GmxAccount,
          diff: oldDiff - amount,
        });
      });

      setOptimisticTokensBalancesUpdates((old) => {
        return updateByKey(old, token, {
          balanceType: TokenBalanceType.GmxAccount,
          isPending: false,
        });
      });
    },
    MultichainTransferIn: (eventData: EventLogData) => {
      const token = eventData.addressItems.items.token;
      const amount = eventData.uintItems.items.amount;

      setWebsocketTokenBalancesUpdates((old) => {
        const oldDiff = old[token]?.diff || 0n;

        return setByKey(old, token, {
          balanceType: TokenBalanceType.GmxAccount,
          diff: oldDiff + amount,
        });
      });

      setOptimisticTokensBalancesUpdates((old) => {
        return updateByKey(old, token, {
          balanceType: TokenBalanceType.GmxAccount,
          isPending: false,
        });
      });
    },
  };

  useEffect(
    function subscribe() {
      if (hasV2LostFocus || !currentAccount) {
        return;
      }

      const unsubscribe = subscribeToV2Events({
        chainId,
        account: currentAccount,
        eventLogHandlers,
      });

      return function cleanup() {
        unsubscribe();
      };
    },
    [chainId, currentAccount, hasV2LostFocus]
  );

  useEffect(
    function subscribeTokenTransferEvents() {
      if (hasPageLostFocus || !currentAccount || !marketTokensAddressesString) {
        return;
      }

      const unsubscribeFromTokenEvents = subscribeToTransferEvents({
        chainId,
        account: currentAccount,
        marketTokensAddresses: marketTokensAddressesString.split("-"),
        onTransfer: (tokenAddress, amount) => {
          setWebsocketTokenBalancesUpdates((old) => {
            const oldDiff = old[tokenAddress]?.diff || 0n;

            return setByKey(old, tokenAddress, {
              balanceType: TokenBalanceType.Wallet,
              diff: oldDiff + amount,
            });
          });

          setOptimisticTokensBalancesUpdates((old) => {
            return updateByKey(old, tokenAddress, { isPending: false });
          });
        },
      });

      return function cleanup() {
        unsubscribeFromTokenEvents();
      };
    },

    [
      chainId,
      currentAccount,
      hasPageLostFocus,
      marketTokensAddressesString,
      setOptimisticTokensBalancesUpdates,
      setWebsocketTokenBalancesUpdates,
    ]
  );

  useEffect(
    function subscribeApproval() {
      if (!currentAccount) {
        return;
      }

      const unsubscribeApproval = subscribeToApprovalEvents({
        chainId,
        account: currentAccount,
        onApprove: (tokenAddress, spender, value, blockNumber) => {
          setApprovalStatuses((old) => ({
            ...old,
            [tokenAddress]: {
              ...old[tokenAddress],
              [spender]: { value, blockNumber },
            },
          }));
          userAnalytics.pushEvent<TokenApproveResultEvent>({
            event: "TokenApproveAction",
            data: {
              action: "ApproveSuccess",
            },
          });
        },
      });

      return function cleanup() {
        unsubscribeApproval();
      };
    },
    [chainId, currentAccount]
  );

  const pollingTaskIdsRef = useRef<Set<string>>(new Set());
  const taskChainIdRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const taskIds = Object.values(pendingExpressTxnParams)
      .map((p) => p.taskId)
      .filter(
        (id): id is string =>
          Boolean(id) && !latestGelatoTaskStatuses.current[id!] && !pollingTaskIdsRef.current.has(id!)
      );

    if (taskIds.length === 0) return;

    for (const taskId of taskIds) {
      if (!taskChainIdRef.current.has(taskId)) {
        taskChainIdRef.current.set(taskId, chainId);
      }

      const taskChainId = taskChainIdRef.current.get(taskId)!;
      const relayer = getGelatoRelayerForChain(taskChainId);

      if (!relayer) continue;

      pollingTaskIdsRef.current.add(taskId);

      (async () => {
        try {
          const receipt = await relayer.waitForReceipt({
            id: taskId,
            timeout: 120_000,
            pollingInterval: 1_000,
            throwOnReverted: true,
          });

          if (isDevelopment()) {
            const { accountSlug, projectSlug } = getTenderlyAccountParams();
            getGelatoTaskDebugInfo(taskId, accountSlug, projectSlug).then((debugInfo) =>
              // eslint-disable-next-line no-console
              console.log("gelatoDebugData", receipt, debugInfo)
            );
          }

          setGelatoTaskStatuses((old) =>
            setByKey(old, taskId, {
              taskId,
              statusCode: StatusCode.Success,
              transactionHash: receipt.transactionHash,
            })
          );
        } catch (e) {
          if (e instanceof TransactionRevertedError) {
            if (isDevelopment()) {
              const { accountSlug, projectSlug } = getTenderlyAccountParams();
              getGelatoTaskDebugInfo(taskId, accountSlug, projectSlug).then((debugInfo) =>
                // eslint-disable-next-line no-console
                console.log("gelatoDebugData reverted", e, debugInfo)
              );
            }

            setGelatoTaskStatuses((old) =>
              setByKey(old, taskId, {
                taskId,
                statusCode: StatusCode.Reverted,
                message: e.errorMessage,
                transactionHash: e.receipt.transactionHash,
                revertData: typeof e.errorData === "string" ? e.errorData : undefined,
              })
            );
          } else if (e instanceof TransactionRejectedError) {
            setGelatoTaskStatuses((old) =>
              setByKey(old, taskId, {
                taskId,
                statusCode: StatusCode.Rejected,
                message: e.errorMessage,
              })
            );
          } else {
            // eslint-disable-next-line no-console
            console.error(e);

            setGelatoTaskStatuses((old) =>
              setByKey(old, taskId, {
                taskId,
                statusCode: StatusCode.Rejected,
                message: e instanceof Error ? e.message : "Task status polling failed",
              })
            );
          }
        } finally {
          pollingTaskIdsRef.current.delete(taskId);
          taskChainIdRef.current.delete(taskId);
        }
      })();
    }
  }, [pendingExpressTxnParams, latestGelatoTaskStatuses, chainId]);

  useEffect(
    function notifyPendingExpressTxn() {
      Object.values(pendingExpressTxnParams).forEach((pendingExpressTxn) => {
        if (pendingExpressTxn.sendFailed && pendingExpressTxn.key && !pendingExpressTxn.isViewed) {
          setPendingExpressTxnParams((old) => updateByKey(old, pendingExpressTxn.key!, { isViewed: true }));
          setOptimisticTokensBalancesUpdates((old) => {
            const newState = { ...old };
            pendingExpressTxn.payTokenAddresses?.forEach((tokenAddress) => {
              delete newState[tokenAddress];
            });
            return newState;
          });
          setPendingPositionsUpdates({});
          return;
        }

        if (pendingExpressTxn.taskId && pendingExpressTxn.key && gelatoTaskStatuses[pendingExpressTxn.taskId]) {
          const status = gelatoTaskStatuses[pendingExpressTxn.taskId].statusCode;

          if (status === StatusCode.Success && pendingExpressTxn.successMessage && !pendingExpressTxn.isViewed) {
            helperToast.success(pendingExpressTxn.successMessage);
            setPendingExpressTxnParams((old) => updateByKey(old, pendingExpressTxn.key!, { isViewed: true }));
          }

          if (status === StatusCode.Reverted || status === StatusCode.Rejected) {
            let isRelayerMetricSent = false;
            let isViewed = false;

            if (pendingExpressTxn.metricId && !pendingExpressTxn.isRelayerMetricSent) {
              const gelatoError = extractGelatoError(gelatoTaskStatuses[pendingExpressTxn.taskId]);

              sendTxnErrorMetric(pendingExpressTxn.metricId, gelatoError, "relayer");

              const executionFeeErrorParams = getIsInsufficientExecutionFeeError(gelatoError);

              if (executionFeeErrorParams.isErrorMatched) {
                const totastContent = getInsufficientExecutionFeeToastContent({
                  minExecutionFee: executionFeeErrorParams.args.minExecutionFee,
                  executionFee: executionFeeErrorParams.args.executionFee,
                  chainId,
                  executionFeeBufferBps,
                  estimatedExecutionGasLimit: pendingExpressTxn.estimatedExecutionGasLimit ?? 0n,
                  txUrl: getGelatoTaskUrl({
                    taskId: pendingExpressTxn.taskId,
                    isDebug: false,
                  }),
                  errorMessage: executionFeeErrorParams.errorData.errorMessage,
                  shouldOfferExpress: false,
                  setIsSettingsVisible,
                });

                // Wait to ensure there is no race condition with the pending order toast
                sleep(500).then(() => {
                  toast.dismiss(pendingOrderToastIdRef.current);
                  helperToast.error(totastContent, {
                    tradingErrorInfo: {
                      actionName: "Express Order",
                      errorData: executionFeeErrorParams.errorData,
                      metricId: pendingExpressTxn.metricId,
                    },
                  });
                });
                isViewed = true;
              }

              const invalidSignatureErrorParams = getIsInvalidSignatureError(gelatoError);

              if (invalidSignatureErrorParams.isErrorMatched) {
                // Wait to ensure there is no race condition with the pending order toast
                sleep(500).then(() => {
                  toast.dismiss(pendingOrderToastIdRef.current);
                  helperToast.error(<InvalidSignatureToastContent />, {
                    tradingErrorInfo: {
                      actionName: "Express Order",
                      errorData: invalidSignatureErrorParams.errorData,
                      metricId: pendingExpressTxn.metricId,
                    },
                  });
                });
                isViewed = true;
              }

              isRelayerMetricSent = true;
            }

            if (pendingExpressTxn.errorMessage && !pendingExpressTxn.isViewed) {
              helperToast.error(pendingExpressTxn.errorMessage, {
                tradingErrorInfo: {
                  actionName: "Express Order",
                  metricId: pendingExpressTxn.metricId,
                },
              });
              isViewed = true;
            }

            if (isViewed || isRelayerMetricSent) {
              setPendingExpressTxnParams((old) =>
                updateByKey(old, pendingExpressTxn.key!, { isViewed, isRelayerMetricSent })
              );
              setOptimisticTokensBalancesUpdates((old) => {
                const newState = { ...old };
                pendingExpressTxn.payTokenAddresses?.forEach((tokenAddress) => {
                  delete newState[tokenAddress];
                });
                return newState;
              });
              setPendingPositionsUpdates({});
            }
          }
        }
      });
    },
    [
      chainId,
      executionFeeBufferBps,
      gelatoTaskStatuses,
      pendingExpressTxnParams,
      provider,
      setIsSettingsVisible,
      setOptimisticTokensBalancesUpdates,
    ]
  );

  const multichainEventsState = useMultichainEvents({
    hasPageLostFocus,
  });

  const [multichainTransferProgress, setMultichainTransferProgress] = useState<
    MultichainTransferProgress<string> | undefined
  >(undefined);

  useMultichainTransferProgressView(multichainTransferProgress);

  useEffect(() => {
    setAwaitingBackfillOrders([]);
  }, [chainId, currentAccount]);

  const handleOrderBackfillMatches = useCallback((matches: OrderBackfillMatch[]) => {
    setOrderStatuses((old) => applyOrderBackfillMatches(old, matches));

    setAwaitingBackfillOrders((old) => old.filter((order) => !matches.some((m) => m.pendingOrder === order)));
  }, []);

  useOrderStatusesBackfill({
    chainId,
    pendingOrders: awaitingBackfillOrders.filter((order) => order.createdAt + ORDER_BACKFILL_MAX_AGE_MS > Date.now()),
    orderStatuses,
    onMatches: handleOrderBackfillMatches,
  });

  const contextState: SyntheticsEventsContextType = useMemo(() => {
    return {
      orderStatuses,
      depositStatuses,
      withdrawalStatuses,
      shiftStatuses,
      approvalStatuses,
      pendingOrdersUpdates,
      pendingPositionsUpdates,
      positionIncreaseEvents,
      positionDecreaseEvents,
      pendingExpressTxns: pendingExpressTxnParams,
      gelatoTaskStatuses,
      setPendingExpressTxn: (params: PendingExpressTxnParams) => {
        setPendingExpressTxnParams((old) => setByKey(old, params.key, params));
      },
      setMultichainTransferProgress,
      updatePendingExpressTxn: (params: Partial<PendingExpressTxnParams>) => {
        setPendingExpressTxnParams((old) => {
          if (!params.key) {
            return old;
          }

          const key = params.key;

          if (old[key]) {
            return updateByKey(old, key, { ...params });
          }

          return setByKey(old, key, params);
        });
      },
      setPendingOrder: (data: PendingOrderData | PendingOrderData[]) => {
        pendingOrderToastIdRef.current = Date.now();

        helperToast.success(
          <OrdersStatusNotificiation
            pendingOrderData={data}
            marketsInfoData={marketsInfoData}
            tokensData={tokensData}
            toastTimestamp={pendingOrderToastIdRef.current}
          />,
          {
            autoClose: false,
            toastId: pendingOrderToastIdRef.current,
          }
        );

        const arrayData = Array.isArray(data) ? data : [data];
        const objData: Record<string, OrderTxnType> = arrayData.reduce(
          (acc, order) => (!order.orderKey ? acc : setByKey(acc, order.orderKey, order.txnType)),
          {}
        );

        setPendingOrdersUpdates((old) => ({ ...old, ...objData }));

        const backfillableOrders = arrayData.filter(getIsPendingOrderBackfillable);

        if (backfillableOrders.length > 0) {
          setAwaitingBackfillOrders((old) => [...old, ...backfillableOrders]);
        }
      },
      setPendingOrderUpdate: (data: PendingOrderData, remove?: "remove") => {
        setPendingOrdersUpdates((old) => {
          if (!data.orderKey) {
            return old;
          }

          return remove ? deleteByKey(old, data.orderKey) : setByKey(old, data.orderKey, "update");
        });
      },
      setPendingFundingFeeSettlement: (data: PendingFundingFeeSettlementData) => {
        const toastId = Date.now();

        helperToast.success(
          <FeesSettlementStatusNotification
            orders={data.orders}
            toastTimestamp={toastId}
            marketsInfoData={marketsInfoData}
          />,
          {
            autoClose: false,
            toastId,
          }
        );
      },
      setPendingDeposit: (data: PendingDepositData) => {
        const toastId = Date.now();

        helperToast.success(
          <GmStatusNotification
            pendingDepositData={data}
            marketsInfoData={glvAndGmMarketsData}
            tokensData={tokensData}
            toastTimestamp={toastId}
          />,
          {
            autoClose: false,
            toastId,
          }
        );
      },
      setPendingWithdrawal: (data: PendingWithdrawalData) => {
        const toastId = Date.now();

        helperToast.success(
          <GmStatusNotification
            pendingWithdrawalData={data}
            marketsInfoData={glvAndGmMarketsData}
            tokensData={tokensData}
            toastTimestamp={toastId}
          />,
          {
            autoClose: false,
            toastId,
          }
        );
      },
      setPendingShift: (data: PendingShiftData) => {
        const toastId = Date.now();

        helperToast.success(
          <GmStatusNotification
            pendingShiftData={data}
            marketsInfoData={marketsInfoData}
            tokensData={tokensData}
            toastTimestamp={toastId}
          />,
          {
            autoClose: false,
            toastId,
          }
        );
      },
      async setPendingPosition(update: PendingPositionUpdate) {
        setPendingPositionsUpdates((old) => setByKey(old, update.positionKey, update));
      },

      setOrderStatusViewed(key: string) {
        setOrderStatuses((old) => updateByKey(old, key, { isViewed: true }));
      },

      setDepositStatusViewed(key: string) {
        setDepositStatuses((old) => updateByKey(old, key, { isViewed: true }));
      },

      setWithdrawalStatusViewed(key: string) {
        setWithdrawalStatuses((old) => updateByKey(old, key, { isViewed: true }));
      },

      setShiftStatusViewed(key: string) {
        setShiftStatuses((old) => updateByKey(old, key, { isViewed: true }));
      },

      ...multichainEventsState,
    };
  }, [
    orderStatuses,
    depositStatuses,
    withdrawalStatuses,
    shiftStatuses,
    approvalStatuses,
    pendingOrdersUpdates,
    pendingPositionsUpdates,
    positionIncreaseEvents,
    positionDecreaseEvents,
    pendingExpressTxnParams,
    gelatoTaskStatuses,
    multichainEventsState,
    marketsInfoData,
    tokensData,
    glvAndGmMarketsData,
  ]);

  return <SyntheticsEventsContext.Provider value={contextState}>{children}</SyntheticsEventsContext.Provider>;
}

function getPendingPositionKeyFromOrder(
  chainId: number,
  marketsInfoData: MarketsInfoData,
  order: Pick<
    OrderCreatedEventData,
    | "account"
    | "marketAddress"
    | "initialCollateralTokenAddress"
    | "swapPath"
    | "shouldUnwrapNativeToken"
    | "isLong"
    | "orderType"
  >
): string | undefined {
  if (isIncreaseOrderType(order.orderType)) {
    const wrappedToken = getWrappedToken(chainId);

    const { outTokenAddress } = getSwapPathOutputAddresses({
      marketsInfoData,
      initialCollateralAddress: order.initialCollateralTokenAddress,
      swapPath: order.swapPath,
      wrappedNativeTokenAddress: wrappedToken.address,
      shouldUnwrapNativeToken: order.shouldUnwrapNativeToken,
      isIncrease: true,
    });

    return outTokenAddress
      ? getPositionKey(order.account, order.marketAddress, outTokenAddress, order.isLong)
      : undefined;
  }

  if (isDecreaseOrderType(order.orderType)) {
    return getPositionKey(order.account, order.marketAddress, order.initialCollateralTokenAddress, order.isLong);
  }

  return undefined;
}
